<script setup lang="ts">
import type { UIDialogInstance } from '../../types'
import type { TransitionProps, HTMLAttributes } from 'vue'
import { onUnmounted, onMounted, watch, computed, ref, useCssVar, getScrollbarWidth, provide, UIDIALOG_KEY, nextTick, toValue, useDevice, shallowRef, useAttrs, useDialogContext, useId, useNuxtApp, useDefaults } from '#imports'
import type { Name as ThemeName } from '#theme'

export interface UIDialogProps {
    absolute?: boolean
    header?: string
    wide?: boolean
    showCloseIcon?: boolean
    showHeader?: boolean
    showFooter?: boolean
    showSelfAssessment?: boolean
    fitContent?: boolean
    alignCenter?: boolean
    closeOnBackdropClick?: boolean
    scrollable?: boolean
    stickyFooter?: boolean
    minWidth?: string
    width?: string
    maxWidth?: string
    minHeight?: string
    height?: string
    maxHeight?: string
    backgroundColor?: string
    takeOver?: boolean
    headerIconComponent?: string
    headerLogoSrc?: string
    transitionName?: 'fade' | 'bounce-down'
    backdropProps?: {
        class?: HTMLAttributes['class']
        style?: HTMLAttributes['style']
    }
    variant?: 'dialog' | 'promotion'
    theme?: ThemeName
}
const _props = withDefaults(defineProps<UIDialogProps>(), {
    absolute: false,
    header: undefined,
    wide: false,
    showCloseIcon: true,
    showHeader: true,
    showFooter: true,
    showSelfAssessment: true,
    fitContent: false,
    alignCenter: false,
    closeOnBackdropClick: true,
    scrollable: true,
    stickyFooter: false,
    minWidth: undefined,
    width: undefined,
    maxWidth: undefined,
    minHeight: '200px',
    height: undefined,
    maxHeight: undefined,
    backgroundColor: undefined,
    transitionName: 'fade',
    headerIconComponent: undefined,
    headerLogoSrc: undefined,
    backdropProps: undefined,
    variant: 'dialog',
})

const props = useDefaults({
    props: _props
})

const emit = defineEmits<{
    open: []
    close: []
}>()

const slots = defineSlots<{
    selfAssessment?(): any
    header?(props: UIDialogInstance): any
    default(props: UIDialogInstance): any
    footer?(props: UIDialogInstance): any
}>()

defineOptions({
    inheritAttrs: false
})

const dialogId = useId()
const attrs = useAttrs()
const { isMobile } = useDevice()
const dialogContext = useDialogContext(dialogId)
const { isHydrating } = useNuxtApp()

const modelValue = defineModel<boolean>({ default: false })
const internalValue = ref<boolean>(modelValue.value)

const backdrop = ref<HTMLDivElement>()
const dialog = ref<HTMLDivElement>()

const scrollY = useCssVar('--ui-body-scroll-y', import.meta.client ? document.documentElement : undefined)
const scrollbarOffset = useCssVar('--ui-scrollbar-offset', import.meta.client ? document.documentElement : undefined)

async function open() {
    if (import.meta.server) {
        dialogContext.open()
    }

    await nextTick()

    /**
     * // Only check for scrollbar width on the first open dialog, since it will be hidden for subsequently opened dialogs,
     * which results in the scrollbarOffset being set to 0px, since the scrollbar is hidden at that moment.
     */
    if (!dialogContext.hasOpenDialogs.value) {
        scrollY.value = `${window.scrollY}px`
        scrollbarOffset.value = `${getScrollbarWidth()}px`
    }

    dialogContext.open()

    if (!modelValue.value) {
        modelValue.value = true
    }
    internalValue.value = true

    emit('open')
}

function close() {
    // Run animations first
    if (props.transitionName === 'bounce-down') {
        if (timeline.value) {
            runBounceDownAnimation(undefined, close)

            return
        }
    }

    dialogContext.close()

    if (modelValue.value) {
        // Prevents unwanted emits if close() was called on a destroy and the dialog was not visible
        emit('close')

        scrollY.value = '0'

        modelValue.value = false
    }

    internalValue.value = false
}

function onBackdropClick() {
    if (!props.closeOnBackdropClick) {
        return
    }

    close()
}

function toggle() {
    if (!modelValue.value) {
        return open()
    }

    return close()
}

/**
 * Manages the updating of `internalValue`, which lets us take full control of the animation flow
 */
function onModelValueChange() {
    if (modelValue.value) {
        open()
    }
    else {
        close()
    }
}

// Animations
const timeline = shallowRef<gsap.core.Timeline>()
const transitionProps = computed<TransitionProps>(() => {
    const value: TransitionProps = {
        appear: import.meta.client && !isHydrating
    }

    if (props.transitionName === 'fade') {
        value.name = 'fade'
        value.mode = 'in-out'
    }

    if (props.transitionName === 'bounce-down') {
        value.onBeforeEnter = runBounceDownAnimation
        value.css = false
    }

    return value
})

const gsapContext = ref<gsap.Context>()

async function runBounceDownAnimation(el?: Element, done?: () => void | undefined) {
    const gsap = (await import('gsap')).default

    const backdropRaw = el || toValue(backdrop)
    const dialogRaw = toValue(dialog)
    if (backdropRaw && dialogRaw) {
        gsapContext.value = gsap.context(() => {
            if (!timeline.value) {
                timeline.value = gsap.timeline().eventCallback('onComplete', () => {
                    if (done) {
                        done()
                    }
                }).to(backdropRaw, { duration: 0.4, backgroundColor: 'rgba(0, 0, 0, 0.5)' })
                    .set(dialogRaw, {
                        height: 'auto',
                    })
                    .to(dialogRaw, {
                        duration: 0.6,
                        y: 0,
                        opacity: 100,
                        ease: isMobile ? 'power3' : 'back.out(1.3)'
                    }, '<')
            }
            else {
                timeline.value.eventCallback('onReverseComplete', () => {
                    timeline.value = undefined

                    if (done) {
                    // Wait until nextTick, to make sure router state is correct etc.
                        nextTick().then(() => {
                            done()
                        })
                    }
                })
                    .timeScale(1.5).reverse()
            }
        })
    }
}

defineExpose({
    open,
    close,
    toggle
})

provide(UIDIALOG_KEY, {
    open, close, toggle
})

if (import.meta.server) {
    onModelValueChange()
}

if (import.meta.client && !isHydrating) {
    onMounted(() => {
        onModelValueChange()
    })
}

onUnmounted(() => {
    if (gsapContext.value) gsapContext.value.revert()
    close()
})

watch(modelValue, () => {
    onModelValueChange()
})
</script>

<template>
    <Teleport to="#teleports">
        <ThemeProvider :theme="props.theme">
            <Transition v-bind="transitionProps">
                <div
                    v-if="internalValue"
                    v-bind="backdropProps"
                    ref="backdrop"
                    class="dialog__backdrop"
                    :class="{
                        'dialog__backdrop--take-over': takeOver,
                        'dialog__backdrop--absolute': absolute,
                        'dialog__backdrop--center': alignCenter,
                        'dialog__backdrop--bounce-down': props.transitionName === 'bounce-down'
                    }"
                    role="dialog"
                    aria-modal="true"
                    data-test="ui-dialog"
                    @click.self="onBackdropClick"
                >
                    <div
                        ref="dialog"
                        class="dialog"
                        tabindex="-1"
                        :class="{
                            'dialog--fit-content': fitContent,
                            'dialog--scrollable': scrollable,
                            'dialog--sticky-footer': stickyFooter,
                            'dialog--wide': wide,
                            'dialog--take-over': takeOver,
                            'dialog--has-max-height': !!maxHeight,
                            'dialog--promotion': variant === 'promotion',
                            'dialog--bounce-down': transitionName === 'bounce-down',
                        }"
                        :style="{
                            minHeight,
                            height,
                            maxHeight,
                            minWidth,
                            width,
                            maxWidth,
                            backgroundColor
                        }"
                        v-bind="attrs"
                    >
                        <div class="dialog__content">
                            <div v-if="showHeader" class="dialog__header">
                                <slot v-if="showSelfAssessment" name="selfAssessment" />

                                <div class="dialog__header-bottom">
                                    <slot
                                        name="header"
                                        v-bind="{
                                            open,
                                            close,
                                            toggle
                                        }"
                                    >
                                        <UIToolbar height="60px">
                                            <template #left>
                                                <NuxtImg
                                                    v-if="headerLogoSrc"
                                                    :src="headerLogoSrc"
                                                    height="34"
                                                    preset="local"
                                                    provider="cloudflare"
                                                    decondig="async"
                                                    loading="lazy"
                                                    alt="Site logo"
                                                    class="dialog__logo"
                                                />
                                            </template>

                                            <template #center>
                                                <h2 v-if="header" v-text="header" />
                                            </template>

                                            <template #right>
                                                <UIButton
                                                    color="link"
                                                    icon
                                                    size="sm"
                                                    @click="close"
                                                >
                                                    <UIIcon
                                                        v-if="showCloseIcon"
                                                        name="close"
                                                        size="16"
                                                        class="dialog__close"
                                                    />
                                                </UIButton>
                                            </template>
                                        </UIToolbar>
                                    </slot>
                                </div>
                            </div>

                            <div
                                class="dialog__body"
                            >
                                <slot v-bind="{ open, close, toggle }" />
                            </div>

                            <div v-if="showFooter && slots.footer" class="dialog__footer">
                                <slot name="footer" v-bind="{ open, close, toggle }" />
                            </div>
                        </div>
                    </div>
                </div>
            </Transition>
        </ThemeProvider>
    </Teleport>
</template>

<style lang="scss" scoped>
.dialog {
    display: block;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: var(--background, $dialog-bg);
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.5);
    user-select: none;
    pointer-events: none;

    @media (min-width: $screen-sm-md) {
        display: flex;
        flex: 1;
        flex-direction: column;
        width: 414px;
        height: min-content;
        max-width: 414px;
        border-radius: var(--border-radius, $border-radius);
        margin: auto;
    }

    &__content {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        contain: layout;
        pointer-events: auto;
        min-height: inherit; // Inherit from parent minHeight prop

        @media (min-width: $screen-sm-md) {
            max-height: 90dvh;
        }
    }

    &__wrapper {
        display: flex;
        flex: 1;
    }

    &__backdrop {
        display: flex;
        position: fixed;
        top: 0;
        bottom: 0;
        left: 0;
        right: 0;
        justify-content: center;
        background: $backdrop;
        width: calc(100% + var(--ui-scrollbar-offset));
        height: 100%;
        z-index: z(dialog);
        user-select: none;
        pointer-events: auto;

        &--take-over {
            z-index: z(take-over);
        }

        &--absolute {
            position: absolute;
        }

        &--center {
            align-items: center;

            // Prevents long texts going out of frame
            .dialog {
                max-height: 100%;
            }
        }

        &--bounce-down {
            background: transparent;
        }
    }

    &__header {
        position: sticky;
        top: 0;
        background: var(--background, $dialog-header-bg);
        z-index: 10;

        h2 {
            margin: 0 auto 0 0;
            font-size: $font-size-base;
            font-weight: $text-heavy;
            text-align: center;
            text-transform: uppercase;

            @media (min-width: $screen-sm) {
                font-size: $font-size-lg;
            }
        }
    }

    &__footer {
        display: flex;
        flex-direction: column-reverse;
        width: 100%;
        padding: 0 30px 30px 30px;
        background: var(--background, $dialog-footer-bg);

        @media (min-width: $screen-sm-md) {
            flex-direction: row;
        }

        .btn {
            width: 100%;
            margin-top: 10px;

            @media (min-width: $screen-sm-md) {
                margin: 0 10px;

                &:first-child {
                    margin-left: 0;
                }

                &:last-child {
                    margin-right: 0;
                }
            }

            &--top-shadow {
                box-shadow: 0 -12px 50px 12px var(--background, #e9eaee);
            }
        }

        .btn-block {
            margin-top: 0 !important; // Must be !important to override Bootstrap
        }
    }

    &__logo {
        height: 34px;
        width: auto;
        margin-right: 8px;
    }

    &__body {
        display: block;
        flex-grow: 1;
        padding: 20px 30px;
        color: var(--on-surface, $dialog-font-color);
        font-size: $font-size-sm;
        user-select: text;

        // Prevents large indent of terms in dialogs
        ol,
        ul {
            padding-left: 15px;
        }
    }

    &__close {
        color: var(--on-surface, $close-btn-default);
        font-size: $font-size-base;

        .icon-sn-arrow-left {
            font-size: $font-size-xl;
        }

        &:hover {
            color: var(--on-surface-hover, $close-btn-hover);
        }
    }

    &--fit-content {
        height: min-content;

        // Prevent body elements from scrolling
        .dialog__body {
            overflow: clip;
        }
    }

    &--loading {
        .dialog__body,
        .dialog__footer {
            opacity: 0.3;
            pointer-events: none;
        }
    }

    &--scrollable {
        .dialog__body {
            overflow-y: auto;
        }
    }

    &--sticky-footer {
        .dialog__footer {
            position: sticky;
            bottom: 0;
            background: var(--background, $dialog-footer-bg);
        }
    }

    &--take-over {
        display: flex;
        flex: 1;
        width: 100%;
        max-width: none;
        height: 100%;
        max-height: none;
        margin: auto;
        border-radius: 0;

        .dialog__header {
            flex: 0;
            padding: 0;
            background: transparent;

            .close {
                position: absolute;
                top: 0;
                right: 0;
                margin: 0;
                padding: 12px;
            }
        }

        .dialog__body,
        .dialog__content {
            display: flex;
            flex: 1;
            flex-direction: column;
            padding: 0;
        }
    }

    &--wide {
        @media (min-width: $screen-sm-md) {
            max-width: 80%;
            margin: auto;
        }

        @media (min-width: $screen-lg) {
            max-width: $screen-md;
        }
    }

    &--has-max-height {
        @media (max-width: #{$screen-sm-md - 1}) {
            align-self: end;
        }
    }

    &--promotion {
        position: relative;
        color: var(--on-surface, $dialog-font-color);
        background: var(--surface, $promotion-dialog-bg);
        border-radius: var(--border-radius-lg, $border-radius-lg) var(--border-radius-lg, $border-radius-lg) 0 0;
        height: min-content;
        align-self: flex-end;

        @media (min-width: $screen-sm-md) {
            border-radius: var(--border-radius-lg, $border-radius-lg);
        }

        .dialog {
            &__footer,
            &__header {
                background: transparent;
            }

            &__header {
                position: absolute;
                top: 0;
                width: 100%;
            }
        }
    }

    &--bounce-down {
        opacity: 0;
        transform: translate(0, -100vh);

        @media (max-width: #{$screen-sm-md - 1}) {
            position: absolute;
            bottom: 0;
            transform: translate(0, 100vh);
        }
    }
}
</style>
